/**
 * PlanLockDialog
 * Global modal shown when a user clicks a CTA blocked by a plan-entitlement
 * or usage-quota restriction. Driven by store/slices/planLockSlice.ts —
 * mounted once in AdminLayout so every admin route can trigger it via
 * usePlanGate() (instant client check) or plan-lock.util.ts (reactive
 * backend-error fallback).
 */

import React from 'react';
import { Dialog, DialogContent, Box, Typography, Button } from '@mui/material';
import { AccountBalanceWallet, Lock, Upgrade } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '@/store';
import { hidePlanLock } from '@/store/slices/planLockSlice';

const PlanLockDialog: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { open, payload } = useSelector((state: RootState) => state.planLock);

  const handleClose = () => dispatch(hidePlanLock());

  const isWallet = payload?.kind === 'wallet';

  const handleUpgrade = () => {
    dispatch(hidePlanLock());
    navigate(isWallet ? '/admin/billing/wallet' : '/admin/billing/active-plan');
  };

  if (!payload) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ textAlign: 'center', p: 4 }}>
        {isWallet ? (
          <AccountBalanceWallet sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
        ) : (
          <Lock sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
        )}
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {payload.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {payload.message}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={isWallet ? <AccountBalanceWallet /> : <Upgrade />}
            onClick={handleUpgrade}
          >
            {isWallet ? 'Top up wallet' : 'Upgrade Plan'}
          </Button>
          <Button variant="text" color="inherit" onClick={handleClose}>
            Maybe later
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PlanLockDialog;
